import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  doc,
  setDoc,
  collection,
  addDoc,
  deleteDoc,
  updateDoc,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useUserStore } from "./userStore";
import { Algo, AlgoStatus } from "@/components/widgets/trading/algos/types";

interface AlgosState {
  algos: Algo[];
  setAlgos: (algos: Algo[]) => void;
  addAlgo: (
    algo: Omit<Algo, "id" | "createdAt" | "updatedAt" | "orders" | "status">,
  ) => Promise<string>;
  updateAlgo: (id: string, updates: Partial<Algo>) => Promise<void>;
  removeAlgo: (id: string) => Promise<void>;
  syncWithFirebase: (uid: string) => Promise<void>;
  selectedAlgoId: string | null;
  setSelectedAlgoId: (id: string | null) => void;
  selectedAlgo: Algo | null;
  subscribeToAlgos: (uid: string) => void;
  _unsubscribeAlgos?: () => void;
}

export const useAlgosStore = create<AlgosState>()(
  persist(
    (set, get) => ({
      algos: [],
      setAlgos: (algos) => set({ algos }),
      addAlgo: async (algoData) => {
        const now = Date.now();
        const algoWithoutId = {
          ...algoData,
          status: "Created" as AlgoStatus,
          orders: [],
          createdAt: now,
          updatedAt: now,
        };
        try {
          const user = useUserStore.getState().user;
          if (!user?.uid) {
            throw new Error("User not authenticated");
          }
          const algosCollection = collection(db, `executor/${user.uid}/algos`);
          const docRef = await addDoc(algosCollection, algoWithoutId);
          return docRef.id;
        } catch (error) {
          console.error("Error adding algo:", error);
          throw error;
        }
      },
      updateAlgo: async (id, updates) => {
        try {
          const user = useUserStore.getState().user;
          if (!user?.uid) {
            throw new Error("User not authenticated");
          }
          const updatesWithTimestamp = { ...updates, updatedAt: Date.now() };
          const algoRef = doc(db, `executor/${user.uid}/algos/${id}`);
          await updateDoc(algoRef, updatesWithTimestamp);
          set((state) => ({
            algos: state.algos.map((algo) =>
              algo.id === id ? { ...algo, ...updatesWithTimestamp } : algo,
            ),
          }));
        } catch (error) {
          console.error("Error updating algo:", error);
          throw error;
        }
      },
      removeAlgo: async (id) => {
        try {
          const user = useUserStore.getState().user;
          if (!user?.uid) {
            throw new Error("User not authenticated");
          }
          const algoRef = doc(db, `executor/${user.uid}/algos/${id}`);
          await deleteDoc(algoRef);
          set((state) => ({
            algos: state.algos.filter((algo) => algo.id !== id),
          }));
        } catch (error) {
          console.error("Error removing algo:", error);
          throw error;
        }
      },
      syncWithFirebase: async (uid: string) => {
        try {
          const algosCollection = collection(db, `executor/${uid}/algos`);
          const snapshot = await getDocs(algosCollection);
          const firestoreAlgos = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          })) as Algo[];
          set({ algos: firestoreAlgos });
        } catch (error) {
          console.error("Error syncing algos with Firebase:", error);
          throw error;
        }
      },
      selectedAlgoId: null,
      setSelectedAlgoId: (id) => set({ selectedAlgoId: id }),
      selectedAlgo: null,
      subscribeToAlgos: (uid: string) => {
        if (!uid) {
          return;
        }
        const algosCollection = collection(db, `executor/${uid}/algos`);
        if (get()._unsubscribeAlgos) {
          get()._unsubscribeAlgos?.();
        }
        const unsubscribe = onSnapshot(algosCollection, (snapshot) => {
          const firestoreAlgos = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          })) as Algo[];
          set({ algos: firestoreAlgos });
        });
        set({ _unsubscribeAlgos: unsubscribe });
      },
      _unsubscribeAlgos: undefined,
    }),
    {
      name: "sf-algos-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

useAlgosStore.subscribe((state) => {
  const selected =
    state.algos.find((algo) => algo.id === state.selectedAlgoId) || null;
  if (state.selectedAlgo !== selected) {
    useAlgosStore.setState({ selectedAlgo: selected });
  }
});
